/*
  Mobile sliders - product gallery and product grids.

  The sliding itself is the browser's own scroll-snap, so the swipe gesture is
  native. This file adds:
    1. <mobile-slider-nav> - dot indicators that stay in sync with the slide the
       customer is on, and jump to a slide when tapped.
    2. A swipe/tap guard so dragging through a slider never triggers the link or
       button under the finger. Only runs below 750px, so desktop is unchanged.
*/

(function mobileSlider() {
  const MOBILE_SLIDER_BREAKPOINT = 749;

  /*
    Stops a swipe from being treated as a tap on the link or button under the
    finger. Applied by <mobile-slider-nav> to its own slider, and swept on load
    for sliders that have no dots.
  */
  function applyMobileSwipeGuard(slider) {
    if (!slider || slider.dataset.swipeGuardReady === 'true') return;
    slider.dataset.swipeGuardReady = 'true';

    const MOVE_THRESHOLD = 8;
    let startX = 0;
    let startY = 0;
    let dragged = false;

    const onStart = (event) => {
      const point = event.touches ? event.touches[0] : event;
      if (!point) return;
      startX = point.clientX;
      startY = point.clientY;
      dragged = false;
    };

    const onMove = (event) => {
      const point = event.touches ? event.touches[0] : event;
      if (!point) return;
      if (Math.abs(point.clientX - startX) > MOVE_THRESHOLD || Math.abs(point.clientY - startY) > MOVE_THRESHOLD) {
        dragged = true;
      }
    };

    if (window.PointerEvent) {
      slider.addEventListener('pointerdown', onStart, { passive: true });
      slider.addEventListener('pointermove', onMove, { passive: true });
    } else {
      slider.addEventListener('touchstart', onStart, { passive: true });
      slider.addEventListener('touchmove', onMove, { passive: true });
    }

    slider.addEventListener(
      'click',
      (event) => {
        if (!dragged) return;
        dragged = false;
        if (window.innerWidth > MOBILE_SLIDER_BREAKPOINT) return;
        if (!event.target.closest('a, button')) return;

        // The customer was swiping, not tapping - don't follow the link.
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  if (!customElements.get('mobile-slider-nav')) {
    customElements.define(
      'mobile-slider-nav',
      class MobileSliderNav extends HTMLElement {
        constructor() {
          super();
          this.onSliderScroll = this.onSliderScroll.bind(this);
          this.onDotClick = this.onDotClick.bind(this);
          this.activeIndex = null;
        }

        connectedCallback() {
          this.slider = this.resolveSlider();
          if (!this.slider) return;

          this.slideSelector = this.dataset.slideSelector || '.grid__item';
          this.dotLabel = this.dataset.dotLabel || 'Slide [index]';

          applyMobileSwipeGuard(this.slider);

          this.addEventListener('click', this.onDotClick);
          this.slider.addEventListener('scroll', this.onSliderScroll, { passive: true });

          // The gallery moves the matching image to the front when a variant with
          // its own image is selected, so the dots have to be rebuilt.
          this.observer = new MutationObserver(() => this.syncDots());
          this.observer.observe(this.slider, { childList: true });

          this.syncDots();
        }

        disconnectedCallback() {
          if (this.slider) this.slider.removeEventListener('scroll', this.onSliderScroll);
          this.removeEventListener('click', this.onDotClick);
          if (this.observer) this.observer.disconnect();
        }

        /*
          Prefers a lookup scoped to this nav's own container. The same product
          can appear twice on a page, and getElementById would hand back the
          first match - pointing these dots at another card's slider.
        */
        resolveSlider() {
          const scopeSelector = this.dataset.sliderScope;

          if (scopeSelector) {
            const scope = this.closest(scopeSelector);
            const local = scope && scope.querySelector('[data-mobile-slider-track]');
            if (local) return local;
          }

          return this.dataset.slider ? document.getElementById(this.dataset.slider) : null;
        }

        get slides() {
          return Array.from(this.slider.querySelectorAll(this.slideSelector));
        }

        get dots() {
          return Array.from(this.querySelectorAll('.mobile-slider__dot'));
        }

        syncDots() {
          const slideCount = this.slides.length;

          if (slideCount < 2) {
            this.hidden = true;
            return;
          }
          this.hidden = false;

          if (this.dots.length !== slideCount) {
            const fragment = document.createDocumentFragment();

            for (let index = 0; index < slideCount; index++) {
              const dot = document.createElement('button');
              dot.type = 'button';
              dot.className = 'mobile-slider__dot';
              dot.dataset.index = index;
              dot.setAttribute('aria-label', this.dotLabel.replace('[index]', index + 1));
              fragment.appendChild(dot);
            }

            this.replaceChildren(fragment);
          }

          this.activeIndex = null;
          this.update();
        }

        onSliderScroll() {
          this.update();
        }

        update() {
          const slides = this.slides;
          const dots = this.dots;
          if (slides.length < 2 || dots.length !== slides.length) return;

          const scrollLeft = this.slider.scrollLeft;
          let activeIndex = 0;
          let smallestDistance = Infinity;

          // Read every position first, then write, so a scroll never interleaves
          // layout reads with class changes.
          slides.forEach((slide, index) => {
            const distance = Math.abs(slide.offsetLeft - scrollLeft);
            if (distance < smallestDistance) {
              smallestDistance = distance;
              activeIndex = index;
            }
          });

          if (activeIndex === this.activeIndex) return;
          this.activeIndex = activeIndex;

          dots.forEach((dot, index) => {
            const isActive = index === activeIndex;
            dot.classList.toggle('is-active', isActive);
            if (isActive) {
              dot.setAttribute('aria-current', 'true');
            } else {
              dot.removeAttribute('aria-current');
            }
          });
        }

        onDotClick(event) {
          const dot = event.target.closest('.mobile-slider__dot');
          if (!dot) return;

          const slide = this.slides[Number(dot.dataset.index)];
          if (!slide) return;

          this.slider.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
        }
      }
    );
  }

  // Sweep on load for sliders rendered without dots (for example when the
  // pagination style is set to arrows and counter).
  (function sweepMobileSwipeGuards() {
    function init() {
      document.querySelectorAll('[data-swipe-guard]').forEach(applyMobileSwipeGuard);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }

    document.addEventListener('shopify:section:load', init);
  })();
})();
